import { createContext, useContext, ReactNode, useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { FontPairing } from 'constants/fontPairings'
import { BrandKit, AIJobStatus } from 'types'
import { StoreSnapshot } from '@tldraw/tldraw'
import { useLastUsedLanguage } from 'hooks/queries/useLastUsedLanguage'
import { useGenerateDesignContentSync } from 'hooks/queries/workspaces/useGenerateDesignContentSync'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { reportError } from 'utils/error'
import { IMediaFile } from 'components/Design/UploadsTab/Types'

interface PreviewState {
  colors: string[]
  fontStyles?: FontPairing
  media?: IMediaFile
  brandKit?: BrandKit

  generatedContent: StoreSnapshot<any> | null
  isGenerating: boolean

  showPreview: boolean

  /* setters / actions */
  setColors: (c: string[]) => void
  setFontStyles: (f: FontPairing) => void
  setMedia: (m?: IMediaFile) => void
  setBrandKit: (b?: BrandKit) => void
  setShowPreview: (b: boolean) => void
  startGeneration: ({ kit, colors }: { kit?: BrandKit; colors?: string[] }) => void
}

export const PreviewCtx = createContext<PreviewState | null>(null)

export const usePreviewState = () => {
  const ctx = useContext(PreviewCtx)
  if (!ctx) throw new Error('usePreviewState must be used within <PreviewProvider>')
  return ctx
}

const paletteFromKit = (kit?: BrandKit) =>
  kit?.brand_visual_kit?.primary_color_palette?.colors?.map((c) => c.hex) ?? ['#000000', '#FFFFFF']

export const PreviewProvider = ({
  children,
  brandKitId,
  disableAutoGeneration = false,
}: {
  children: ReactNode
  brandKitId?: number
  disableAutoGeneration?: boolean
}) => {
  const [colors, setColorsInternal] = useState<string[]>(['#000000', '#FFFFFF'])
  const [colorsManuallySet, setColorsManuallySet] = useState<boolean>(false)

  const setColors = useCallback((newColors: string[]) => {
    setColorsInternal(newColors)
    setColorsManuallySet(true)
  }, [])
  const [fontStyles, setFontStyles] = useState<FontPairing | undefined>()
  const [media, setMedia] = useState<IMediaFile | undefined>()
  const [brandKit, setBrandKit] = useState<BrandKit | undefined>()
  const [showPreview, setShowPreview] = useState<boolean>(false)

  const [generatedContent, setGeneratedContent] = useState<StoreSnapshot<any> | null>(null)
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const controllerRef = useRef<AbortController | null>(null)

  const { data: defaultLanguageQuery } = useLastUsedLanguage()
  const defaultLanguage = defaultLanguageQuery?.language
  const generateMutation = useGenerateDesignContentSync({ retry: 3 })

  const { data: brandKitResponse } = useBrandKit(brandKitId, {
    includeAllMediaFiles: true,
    enabled: !!brandKitId,
  })

  useEffect(() => {
    if (!brandKitResponse?.brand_kit) return
    setBrandKit(brandKitResponse.brand_kit)
    /* choose first media as hero, if any */
    if (!media && brandKitResponse.brand_kit.media_files.length) {
      setMedia(brandKitResponse.brand_kit.media_files[0])
    }
  }, [brandKitResponse, media])

  const startGeneration = useCallback(
    ({ kit = brandKit, colors }: { kit?: BrandKit; colors?: string[] }) => {
      if (!kit || isGenerating) return

      setIsGenerating(true)
      const controller = new AbortController()
      controllerRef.current = controller

      generateMutation.mutate(
        {
          source_visual_element_id: 8448,
          topic: kit.websites?.[0]?.source_materials?.[0]?.text ?? '',
          language: defaultLanguage ?? undefined,
          brand_voice_id: kit.brand_voice?.id,
          brand_kit_id: kit.id,
          brand_visual_kit_id: kit.brand_visual_kit?.id,
          initial_media_file_id: media?.id,
          initial_media_file_remove_background: false,
          apply_brand: true,
          signal: controller.signal,
        },
        {
          onSuccess: (data) => {
            try {
              setGeneratedContent(JSON.parse(data.generated_content ?? '{}'))
            } catch (err) {
              reportError(err)
            }
            setColors(colors ?? paletteFromKit(kit))
            setIsGenerating(false)
          },
          onError: () => setIsGenerating(false),
        },
      )
    },
    [brandKit, isGenerating, generateMutation, defaultLanguage, media, setColors],
  )

  useEffect(() => () => controllerRef.current?.abort(), [])

  useEffect(() => {
    if (
      brandKit &&
      brandKit.status === AIJobStatus.Successful &&
      !generatedContent &&
      !isGenerating &&
      !disableAutoGeneration
    ) {
      setShowPreview(true)
      startGeneration({ kit: brandKit })
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [brandKit, generatedContent, isGenerating, disableAutoGeneration])

  useEffect(() => {
    if (brandKit && !colorsManuallySet) {
      const kitColors = paletteFromKit(brandKit)
      setColorsInternal(kitColors)
    }
  }, [brandKit, colorsManuallySet])

  /* ----------------------------- memoised ------------------------------ */
  const value = useMemo(
    () => ({
      colors,
      fontStyles,
      media,
      brandKit,
      generatedContent,
      isGenerating,
      showPreview,
      setColors,
      setFontStyles,
      setMedia,
      setBrandKit,
      setShowPreview,
      startGeneration,
    }),
    [colors, fontStyles, media, brandKit, generatedContent, isGenerating, showPreview, startGeneration, setColors],
  )

  return <PreviewCtx.Provider value={value}>{children}</PreviewCtx.Provider>
}
